/**
 * The connect shelf: long-press a connection point, choose what goes there.
 *
 * A horizontally scrolling shelf of module cards opens over the bottom of the
 * viewport, and whichever card sits in the centre is "tried on": the module
 * is placed off-screen, selected, and previewed at the connection point
 * through the existing preview machinery, so the robot itself shows the
 * candidate at real size and pose. Swiping the scene above the shelf moves to
 * the next card, so the whole flow works without looking away from the robot.
 *
 * The ghost is a real (parked) instance because previewing needs one: the
 * server's preview moves an instance's meshes to the would-be pose. Parked
 * far outside the work area it never shows except through the preview, and it
 * is deleted whenever the candidate changes or the shelf closes without
 * connecting.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Box, Button, Group, Text } from "@mantine/core";
import { IconRotateClockwise2, IconX } from "@tabler/icons-react";
import type { ModuleData } from "../types";
import {
  useAssetsBaseUrl,
  useConnectShelfTarget,
  useInstances,
  useModules,
  useRecentConnectModules,
  useSelectedModuleInstanceId,
  useSelectedModuleRootLink,
  useViewerStore,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { recordConnect, recordMove } from "../hooks";

/** Out of sight until the preview poses it at the connection point. */
export const GHOST_PARK_POSITION: [number, number, number] = [1000, 1000, 0];

/** How long a card must rest in the centre before it is tried on. */
const SETTLE_MS = 250;

/** A touch travelling at least this far, mostly sideways, moves the shelf. */
const SWIPE_THRESHOLD_PX = 60;

/** The yaw stepper walks these, in degrees. */
const YAW_STEP_DEG = 90;

const CARD_WIDTH = 128;
const CARD_GAP = 10;

/**
 * Order the shelf: what was connected recently comes first.
 *
 * Assembling is repetitive - five fingers, four legs - so the last-used
 * modules are overwhelmingly the next-used ones. Ties keep the list order.
 */
export function orderCandidates(modules: ModuleData[], recent: string[]): ModuleData[] {
  const rank = new Map(recent.map((id, index) => [id, index]));
  return [...modules].sort((a, b) => {
    const ra = rank.has(a.id) ? (rank.get(a.id) as number) : Number.POSITIVE_INFINITY;
    const rb = rank.has(b.id) ? (rank.get(b.id) as number) : Number.POSITIVE_INFINITY;
    return ra - rb;
  });
}

/**
 * Same rule as the module library: a series chip keeps its own series and
 * the passive modules (no servo at all), which run on any bus.
 */
export function matchesSeries(module: ModuleData, series: string | null): boolean {
  return series === null || !module.servo_series || module.servo_series === series;
}

/** A short click through WebAudio: iOS has no vibration to answer a connect. */
function playConnectClick(): void {
  try {
    const AudioContextClass =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }
    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.value = 1800;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.07);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.08);
    oscillator.onended = () => context.close();
  } catch {
    // No audio is no reason to fail a connect.
  }
}

/** The card whose centre is nearest the scroller's centre. */
export function centeredIndex(scrollLeft: number, viewportWidth: number, count: number): number {
  const centre = scrollLeft + viewportWidth / 2;
  const stride = CARD_WIDTH + CARD_GAP;
  const index = Math.round((centre - viewportWidth / 2 - CARD_WIDTH / 2) / stride);
  return Math.max(0, Math.min(count - 1, index));
}

/** Scroll offset that puts card `index` in the centre. */
export function scrollOffsetFor(index: number): number {
  return index * (CARD_WIDTH + CARD_GAP);
}

interface CardProps {
  module: ModuleData;
  assetsBaseUrl: string;
  isCentered: boolean;
  onTap: () => void;
}

function ShelfCard({ module, assetsBaseUrl, isCentered, onTap }: CardProps) {
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const videoUrl =
    module.video_url ?? `${assetsBaseUrl}/360_images/${module.name}/${module.name}_360.mp4`;
  const posterUrl = module.poster_url;

  // Only the centred card carries a video element at all. Mounting dozens of
  // them made the browser throttle their loading and every card sat white
  // until its metadata trickled in; the poster is a static image that paints
  // immediately, and the spin only needs to exist where it plays.
  const showVideo = isCentered && !videoFailed;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) {
      return;
    }
    video.play()?.catch(() => undefined);
  }, [showVideo]);

  return (
    <Box
      onClick={onTap}
      style={{
        width: CARD_WIDTH,
        flexShrink: 0,
        scrollSnapAlign: "center",
        borderRadius: 12,
        overflow: "hidden",
        border: isCentered
          ? "3px solid var(--mantine-color-orange-6)"
          : "1px solid var(--mantine-color-default-border)",
        opacity: isCentered ? 1 : 0.6,
        background: "var(--mantine-color-body)",
        cursor: "pointer",
      }}
    >
      <Box
        style={{
          width: "100%",
          aspectRatio: "1",
          background: "var(--mantine-color-default)",
          position: "relative",
        }}
      >
        {posterUrl && !posterFailed ? (
          <img
            src={posterUrl}
            alt={module.name}
            loading="lazy"
            onError={() => setPosterFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Group justify="center" align="center" style={{ height: "100%" }}>
            <Text size="xs" c="dimmed" ta="center" px={6}>
              {module.name}
            </Text>
          </Group>
        )}
        {showVideo && (
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            poster={posterUrl ?? undefined}
            onError={() => setVideoFailed(true)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
      </Box>
      <Text size="xs" ta="center" lineClamp={1} px={4} py={2}>
        {module.name}
      </Text>
    </Box>
  );
}

export function ConnectShelf() {
  const target = useConnectShelfTarget();
  const modules = useModules();
  const recentConnectModules = useRecentConnectModules();
  const assetsBaseUrl = useAssetsBaseUrl();
  const instances = useInstances();
  const selectedInstanceId = useSelectedModuleInstanceId();
  const rootLink = useSelectedModuleRootLink();
  const closeConnectShelf = useViewerStore((state) => state.closeConnectShelf);
  const recordRecentConnectModule = useViewerStore((state) => state.recordRecentConnectModule);
  const flashConnectSnap = useViewerStore((state) => state.flashConnectSnap);
  const {
    sendAddModule,
    sendDeleteInstance,
    sendSelectInstance,
    sendPreviewConnection,
    sendConnectModules,
  } = useWebSocket();

  const [index, setIndex] = useState(0);
  const [yawDeg, setYawDeg] = useState(0);
  const [seriesFilter, setSeriesFilter] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const settleTimerRef = useRef<number | null>(null);

  // The candidate being tried on right now. null between candidates.
  const [ghost, setGhost] = useState<{ id: string; moduleId: string } | null>(null);
  // Mirror for effect cleanups, which must read the ghost synchronously: a
  // setGhost updater runs on the next render, by which time the flags below
  // have been reset and the decision would be taken against fresh state.
  const ghostRef = useRef(ghost);
  ghostRef.current = ghost;
  const ghostSerial = useRef(0);
  const connectedRef = useRef(false);

  // Only what can actually hang off a point: a base module has no input
  // port, and offering it here would end in a connect that goes nowhere.
  // Within that, the series chips narrow to one servo bus (passive modules
  // pass every chip), and what was connected recently comes first.
  const attachable = useMemo(() => modules.filter((module) => module.can_be_child), [modules]);
  const seriesOptions = useMemo(
    () =>
      [...new Set(attachable.map((module) => module.servo_series).filter((s): s is string => Boolean(s)))].sort(),
    [attachable]
  );
  const candidates = useMemo(
    () => orderCandidates(attachable.filter((module) => matchesSeries(module, seriesFilter)), recentConnectModules),
    [attachable, seriesFilter, recentConnectModules]
  );
  const centeredModule = candidates[Math.min(index, Math.max(0, candidates.length - 1))] ?? null;

  // A narrower filter can strand the index past the end; snap back to the
  // front, which is also where the recently-used candidates live.
  useEffect(() => {
    setIndex(0);
    scrollerRef.current?.scrollTo?.({ left: 0 });
  }, [seriesFilter]);

  const ghostPlaced = ghost !== null && instances.some((inst) => inst.instance_id === ghost.id);
  const ghostSelected = ghost !== null && selectedInstanceId === ghost.id;

  // 1. When the centred card settles, replace the ghost with that module.
  useEffect(() => {
    if (!target || !centeredModule) {
      return;
    }
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = window.setTimeout(() => {
      setGhost((current) => {
        if (current?.moduleId === centeredModule.id) {
          return current;
        }
        if (current) {
          sendDeleteInstance(current.id);
        }
        ghostSerial.current += 1;
        // The id is permanent once connected, and prefixes and joint names
        // derive from it - so it has to look like every other instance id
        // (the server's make_uuid: "i" + 8 uuid chars), not like a ghost.
        const id = `i${crypto.randomUUID().slice(0, 8)}`;
        sendAddModule(centeredModule.id, GHOST_PARK_POSITION, id);
        return { id, moduleId: centeredModule.id };
      });
    }, SETTLE_MS);
    return () => {
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, [target, centeredModule, sendAddModule, sendDeleteInstance]);

  // 2. The ghost has landed in the instance list: select it, so the server
  //    sends its root link (which the preview and the connect both need).
  useEffect(() => {
    if (ghost && ghostPlaced && !ghostSelected) {
      sendSelectInstance(ghost.id);
    }
  }, [ghost, ghostPlaced, ghostSelected, sendSelectInstance]);

  // 3. The ghost is selected and its root link is known: try it on.
  useEffect(() => {
    if (!target || !ghost || !ghostSelected || !rootLink) {
      return;
    }
    sendPreviewConnection(
      ghost.id,
      rootLink.name,
      target.instance_id,
      target.link_name,
      true,
      (yawDeg * Math.PI) / 180
    );
  }, [target, ghost, ghostSelected, rootLink, yawDeg, sendPreviewConnection]);

  // Leaving the shelf without connecting takes the ghost with it. The
  // decision is taken here, synchronously: connectedRef is reset by the next
  // run of this effect, so deferring the check into a state updater would
  // read the reset value and delete a module that was just connected.
  useEffect(() => {
    connectedRef.current = false;
    return () => {
      const current = ghostRef.current;
      if (current && !connectedRef.current) {
        sendDeleteInstance(current.id);
        sendSelectInstance(null);
      }
      setGhost(null);
    };
  }, [target, sendDeleteInstance, sendSelectInstance]);

  const step = useCallback(
    (delta: number) => {
      setIndex((current) => {
        const next = Math.max(0, Math.min(candidates.length - 1, current + delta));
        if (next !== current) {
          navigator.vibrate?.(8);
        }
        scrollerRef.current?.scrollTo?.({ left: scrollOffsetFor(next), behavior: "smooth" });
        return next;
      });
    },
    [candidates.length]
  );

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    setIndex(centeredIndex(scroller.scrollLeft, scroller.clientWidth, candidates.length));
  }, [candidates.length]);

  const handleConnect = useCallback(() => {
    if (!target || !ghost || !ghostSelected || !rootLink) {
      return;
    }
    connectedRef.current = true;
    recordRecentConnectModule(ghost.moduleId);
    // The dock lands: ring at the point, a click for iOS (which has no
    // vibration), a buzz where vibration exists.
    flashConnectSnap([target.position[0], target.position[1], target.position[2]]);
    playConnectClick();
    navigator.vibrate?.(25);
    sendConnectModules(
      ghost.id,
      rootLink.name,
      target.instance_id,
      target.link_name,
      (yawDeg * Math.PI) / 180
    );
    // The ghost's own position is the far-away park, which is where a plain
    // connect-undo would strand it. Recording a move first makes the two
    // merge into connect_with_move, whose undo puts the module down next to
    // the point it was attached to.
    recordMove(ghost.id, [target.position[0] + 0.15, target.position[1] + 0.15, target.position[2]]);
    recordConnect(ghost.id);
    closeConnectShelf();
  }, [
    target,
    ghost,
    ghostSelected,
    rootLink,
    yawDeg,
    sendConnectModules,
    closeConnectShelf,
    recordRecentConnectModule,
    flashConnectSnap,
  ]);

  // The C gesture: a decisively horizontal touch swipe on the scene moves the
  // shelf. Listened for at the window and never preventDefault-ed, so the
  // scene stays fully live while the shelf is open - the camera still orbits,
  // modules still drag. (The first shelf blocked the whole viewport with a
  // modal layer, which read as "the viewer stopped rotating".)
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    if (!target) {
      return;
    }
    let start: { x: number; y: number; time: number } | null = null;
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1 && (event.target as Element)?.tagName === "CANVAS") {
        const touch = event.touches[0];
        start = { x: touch.clientX, y: touch.clientY, time: event.timeStamp };
      } else {
        start = null;
      }
    };
    const handleTouchEnd = (event: TouchEvent) => {
      const begin = start;
      start = null;
      if (!begin || event.changedTouches.length !== 1) {
        return;
      }
      const touch = event.changedTouches[0];
      const dx = touch.clientX - begin.x;
      const dy = touch.clientY - begin.y;
      // Decisively horizontal and quick, so slow orbiting does not page the
      // shelf as a side effect.
      if (
        Math.abs(dx) >= SWIPE_THRESHOLD_PX &&
        Math.abs(dx) > 2 * Math.abs(dy) &&
        event.timeStamp - begin.time < 500
      ) {
        stepRef.current(dx < 0 ? 1 : -1);
      }
    };
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [target]);

  if (!target) {
    return null;
  }

  return (
    <>
      <Box
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 600,
          background: "var(--mantine-color-body)",
          borderTop: "1px solid var(--mantine-color-default-border)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <Group justify="space-between" px="md" pt={8} pb={2} wrap="nowrap">
          <Text size="xs" c="dimmed" lineClamp={1}>
            {target.module_name} / {target.link_name} に接続
          </Text>
          <Group gap={4} wrap="nowrap">
            {seriesOptions.length > 0 && (
              <>
                <Button
                  size="compact-xs"
                  radius="xl"
                  variant={seriesFilter === null ? "filled" : "default"}
                  color="orange"
                  onClick={() => setSeriesFilter(null)}
                >
                  すべて
                </Button>
                {seriesOptions.map((series) => (
                  <Button
                    key={series}
                    size="compact-xs"
                    radius="xl"
                    variant={seriesFilter === series ? "filled" : "default"}
                    color="orange"
                    onClick={() => setSeriesFilter(series)}
                    style={{ textTransform: "capitalize" }}
                  >
                    {series}
                  </Button>
                ))}
              </>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Close connect shelf"
              onClick={closeConnectShelf}
            >
              <IconX size={18} />
            </ActionIcon>
          </Group>
        </Group>

        <Box
          ref={scrollerRef}
          onScroll={handleScroll}
          style={{
            display: "flex",
            gap: CARD_GAP,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            // Half-viewport padding lets the first and last card reach the
            // centre, where selection happens.
            padding: `4px calc(50% - ${CARD_WIDTH / 2}px) 8px`,
          }}
        >
          {candidates.map((module, moduleIndex) => (
            <ShelfCard
              key={module.id}
              module={module}
              assetsBaseUrl={assetsBaseUrl}
              isCentered={moduleIndex === index}
              onTap={() => step(moduleIndex - index)}
            />
          ))}
        </Box>

        <Group justify="center" gap="md" pb={10}>
          <Button
            variant="default"
            size="sm"
            leftSection={<IconRotateClockwise2 size={16} />}
            onClick={() => setYawDeg((current) => (current + YAW_STEP_DEG) % 360)}
          >
            {yawDeg}°
          </Button>
          <Button
            size="sm"
            color="orange"
            disabled={!ghostSelected || !rootLink}
            onClick={handleConnect}
          >
            ここに接続
          </Button>
        </Group>
      </Box>
    </>
  );
}
